import React, { useState, useEffect } from 'react';
import BaseModal from './BaseModal'; // Import BaseModal
import { base64ToImage } from '../utils/VerbatimUtils'; // Import utility function for image conversion
import './ExportVerbatimsModal.css';

const ExportVerbatimsModal = ({ show, verbatims, setVerbatims, onClose, onConfirm }) => {
  const [problematicVerbatims, setProblematicVerbatims] = useState([]);

  useEffect(() => {
    if (show) {
      // Identify verbatims with invalid labels
      const problematic = verbatims.filter((verbatim) =>
        Object.values(verbatim.labels || {}).some((labelsArray) =>
          labelsArray?.some((label) => !label || !label.name)
        )
      );
      setProblematicVerbatims(problematic);
    }
  }, [show, verbatims]);

  const handleFixAndExport = () => {
    // Fix issues
    const fixedVerbatims = verbatims.map((verbatim) => {
      const updatedLabels = Object.entries(verbatim.labels || {}).reduce(
        (acc, [labelType, labelsArray]) => {
          // Filter valid labels and remove the label type group if no valid labels remain
          const validLabels = labelsArray.filter((label) => label && label.name);
          if (validLabels.length > 0) {
            acc[labelType] = validLabels;
          }
          return acc;
        },
        {}
      );

      return { ...verbatim, labels: updatedLabels };
    });

    // Update the verbatims and proceed with export
    setVerbatims(fixedVerbatims);

    // Pass the fixed verbatims to onConfirm
    onConfirm(true);
  };

  const renderTable = () => {
    if (problematicVerbatims.length === 0) {
      return <p>No issues detected. Ready to export!</p>;
    }

    return (
      <table className="problematic-verbatims-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Verbatim</th>
            <th>Problematic Labels</th>
          </tr>
        </thead>
        <tbody>
          {problematicVerbatims.map((verbatim) => (
            <tr key={verbatim.id}>
              <td>{verbatim.id}</td>
              <td>
                {typeof verbatim.verbatim === 'string' && verbatim.verbatim}
                {Array.isArray(verbatim.verbatim) && verbatim.verbatim.map((item, index) => {
                    if (item.type === 'text' && item.content.trim() !== '') {
                        return <span key={index}>{item.content}</span>;
                    } else if (item.type === 'image' && item.content) {
                        return (
                            <div key={index} className="review-image-block">
                                <img 
                                    src={base64ToImage(item.content)} 
                                    alt="Verbatim" 
                                    className="review-verbatim-image"
                                    onError={(e) => {
                                    console.error("Erreur de chargement de l'image", e);
                                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                                    }}
                                />
                            </div>
                        );
                    }
                    return null;
                })}
              </td>
              <td>
                {Object.entries(verbatim.labels || {})
                  .filter(([_, labelsArray]) =>
                    labelsArray?.some((label) => !label || !label.name)
                  )
                  .map(([labelType, labelsArray]) => (
                    <div key={labelType}>
                      <strong>{labelType}:</strong>{' '}
                      <span>
                        <em>Issue: Invalid or null label(s) found.</em>
                      </span>
                      <br />
                      {labelsArray
                        .filter((label) => !label || !label.name)
                        .map((_, index) => (
                          <span key={index}>
                            <em>Null Label</em> (Index {index})
                          </span>
                        ))
                        .join(', ')}
                    </div>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (!show) return null;

  return (
    <BaseModal
      show={show}
      title="Export Verbatims - Issues Detected"
      onClose={onClose}
      primaryActionLabel="Fix and Export"
      secondaryActionLabel="Cancel"
      onConfirm={handleFixAndExport}
      onSecondaryAction={onClose}
    >
      <div className="exportverbatimsmodal-content-wrapper">
        <p className="exportverbatimsmodal-note">
          Note: If your verbatims include images, they will be exported. However, if you re-import the Excel file, images will not be taken into account. It is recommended to use the "Save as JSON" feature if you plan to re-import a project.
        </p>
        <p>
          Before exporting, we detected some verbatims with invalid (null) labels. 
          These issues will be fixed automatically when you proceed with the export.
        </p>
        {renderTable()}
      </div>
    </BaseModal>
  );
};

export default ExportVerbatimsModal;
